import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, LogOut, LayoutDashboard, TrendingUp, Users, Eye, EyeOff, Globe, MapPin, QrCode } from 'lucide-react';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { AGENT_INFO } from '../../constants';
import ShareModal from '../../components/ShareModal';

const ToggleSwitch = ({ active, onClick, labelOn = 'En Ligne', labelOff = 'Masqué' }) => {
    return (
        <div
            onClick={onClick}
            className="flex items-center gap-3 cursor-pointer group select-none"
        >
            <span className={`text-[9px] font-black uppercase tracking-widest transition-colors ${active ? 'text-green-600' : 'text-gray-400'}`}>
                {active ? labelOn : labelOff}
            </span>
            <div className={`relative w-10 h-5 rounded-full transition-all duration-300 ${active ? 'bg-green-500 shadow-sm shadow-green-200' : 'bg-gray-200'}`}>
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-transform duration-300 shadow-sm ${active ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
        </div>
    );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const Dashboard = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ visitors24h: 0, views24h: 0, visitors7d: 0, visitors30d: 0 });
    const [notTrackingMe, setNotTrackingMe] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareModalConfig, setShareModalConfig] = useState({ title: '', url: '' });
    const siteTarget = import.meta.env.VITE_SITE_TARGET || 'avantis';
    const publishedKey = `is_published_${siteTarget}`;

    // Gestion des villes
    const [cities, setCities] = useState([]);

    useEffect(() => {
        fetchProperties();
        fetchStats();
        fetchCities();
        setNotTrackingMe(localStorage.getItem('admin_opt_out') === 'true');
    }, []);

    const fetchCities = async () => {
        const { data } = await supabase.from('cities').select('*').order('name');
        if (data) setCities(data);
    };

    const toggleOptOut = () => {
        const newValue = !notTrackingMe;
        setNotTrackingMe(newValue);
        localStorage.setItem('admin_opt_out', String(newValue));
        alert(newValue ? 'Vos visites ne seront plus comptées.' : 'Vos visites sont de nouveau comptées.');
    };

    const fetchStats = async () => {
        try {
            const { data } = await supabase.rpc('get_dashboard_stats');
            if (data) setStats({ visitors24h: data.visitors24h || 0, views24h: data.views24h || 0, visitors7d: data.visitors7d || 0, visitors30d: data.visitors30d || 0 });
        } catch { }
    };

    // Récupère uniquement les biens Avantis (saisie manuelle)
    const fetchProperties = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('properties')
            .select('*')
            .eq('site_target', siteTarget)
            .order('created_at', { ascending: false });
        if (data) setProperties(data);
        setLoading(false);
    };

    const onlineCount = properties.filter(p => p[publishedKey]).length;

    // ─── Actions ──────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce bien ?')) {
            await supabase.from('properties').delete().eq('id', id);
            fetchProperties();
        }
    };

    const handleTogglePublish = async (property) => {
        const willPublish = !property[publishedKey];

        if (willPublish) {
            const cityName = property.city?.trim();
            if (cityName) {
                const exists = cities.some(c => c.name.toLowerCase() === cityName.toLowerCase());
                if (!exists) {
                    try {
                        const { data: newCity, error: cityError } = await supabase
                            .from('cities')
                            .insert([{ name: cityName, sector: 'Luxembourg' }])
                            .select()
                            .single();
                        if (!cityError && newCity) {
                            setCities(prev => [...prev, newCity]);
                        }
                    } catch (err) {
                        console.error('Erreur création automatique ville:', err);
                    }
                }
            }
        }

        const updatePayload = { [publishedKey]: willPublish };
        const { error } = await supabase
            .from('properties')
            .update(updatePayload)
            .eq('id', property.id);

        if (!error) {
            setProperties(prev => prev.map(p =>
                p.id === property.id ? { ...p, ...updatePayload } : p
            ));
        }
    };

    const handleToggleFavorite = async (id) => {
        const target = properties.find(p => p.id === id);
        const on = !target.is_favorite;
        if (on) await supabase.from('properties').update({ is_favorite: false }).neq('id', id);
        await supabase.from('properties').update({ is_favorite: on }).eq('id', id);
        fetchProperties();
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">

            {/* ── Navbar ─────────────────────────────────────────────────── */}
            <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-30 shadow-sm">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="bg-[#1A2A3A] text-white p-2 rounded-lg">
                            <LayoutDashboard size={24} />
                        </div>
                        <div>
                            <h1 className="font-black text-[#1A2A3A] text-xl leading-none">Espace Avantis</h1>
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Gestion Agence Luxembourg</p>
                        </div>
                    </div>
                    <div className="flex gap-4 items-center">
                        <Link to="/" target="_blank">
                            <Button variant="ghost" className="hidden md:flex">Voir le site</Button>
                        </Link>
                        <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                            <LogOut size={24} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">

                {/* ── Stat Cards ──────────────────────────────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                    <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">État de la Vitrine</p>
                            <div className="flex items-baseline gap-4">
                                <p className="text-4xl font-black text-[#1A2A3A]">{onlineCount}</p>
                                <p className="text-sm text-gray-400 font-bold uppercase tracking-widest">Biens en ligne</p>
                            </div>
                            <p className="text-xs text-gray-400 mt-2 font-medium italic">Sur un total de {properties.length} biens saisis</p>
                        </div>
                        <div className="w-16 h-16 bg-blue-50 text-[#EA723D] rounded-2xl flex items-center justify-center">
                            <Globe size={32} />
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-[#EA723D] to-[#1A2A3A] p-8 rounded-2xl shadow-lg text-white flex items-center justify-between">
                        <div>
                            <p className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">Action rapide</p>
                            <h3 className="text-xl font-black mb-1">Prêt à publier ?</h3>
                            <p className="text-white/40 text-xs italic">Ajoutez un nouveau bien en quelques clics</p>
                        </div>
                        <button 
                            onClick={() => navigate('/admin/new')}
                            className="bg-white text-[#EA723D] p-4 rounded-xl shadow-xl hover:scale-110 transition-transform"
                        >
                            <Plus size={24} />
                        </button>
                    </div>
                </div>

                {/* ── QR Codes & Share Panel ── */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                        <h3 className="font-bold text-[#1A2A3A] text-base">Partage rapide du site</h3>
                        <p className="text-xs text-gray-400">Générez des QR Codes pour vos supports imprimés ou partagez vos sites en un clic.</p>
                    </div>
                    <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                        <Button
                            variant="custom"
                            className="bg-[#1A2A3A] hover:bg-[#101b26] text-white flex-1 sm:flex-initial flex items-center justify-center gap-2 rounded-xl text-xs font-bold uppercase px-4 py-3"
                            onClick={() => {
                                setShareModalConfig({
                                    title: AGENT_INFO.name || 'Mon Site Agent',
                                    url: window.location.origin
                                });
                                setIsShareModalOpen(true);
                            }}
                        >
                            <QrCode size={16} /> QR Code Mon Site
                        </Button>
                        <Button
                            variant="custom"
                            className="bg-[#EA723D] hover:bg-[#d85e2b] text-white flex-1 sm:flex-initial flex items-center justify-center gap-2 rounded-xl text-xs font-bold uppercase px-4 py-3"
                            onClick={() => {
                                setShareModalConfig({
                                    title: 'Portail SCBI.EU',
                                    url: 'https://scbi.eu'
                                });
                                setIsShareModalOpen(true);
                            }}
                        >
                            <QrCode size={16} /> QR Code Portail
                        </Button>
                    </div>
                </div>

                {/* ── Titre + action ──────────────────────────────────────── */}
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
                    <h2 className="text-3xl font-black text-[#1A2A3A]">
                        Mes Biens <span className="text-[#EA723D] ml-2">({properties.length})</span>
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                        <Button
                            onClick={() => navigate('/admin/cities')}
                            className="rounded-xl bg-white border-2 border-gray-100 text-[#1A2A3A] font-black hover:bg-gray-50 transition-all w-full md:w-auto justify-center"
                            variant="custom"
                        >
                            <MapPin size={20} className="mr-2 text-[#EA723D]" /> GÉRER LES VILLES
                        </Button>
                        <Button
                            onClick={() => navigate('/admin/new')}
                            className="rounded-xl bg-[#EA723D] hover:bg-[#d6602d] text-white font-black shadow-lg shadow-orange-200/50 w-full md:w-auto justify-center"
                            variant="custom"
                        >
                            <Plus size={20} className="mr-2" /> AJOUTER BIEN
                        </Button>
                    </div>
                </div>

                {/* ── Table ───────────────────────────────────────────────── */}
                {loading ? (
                    <div className="text-center py-20 text-gray-400">Chargement des biens...</div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] text-gray-500 uppercase font-black tracking-widest">
                                        <th className="p-4">Bien</th>
                                        <th className="p-4">Prix</th>
                                        <th className="p-4">Ville</th>
                                        <th className="p-4">Statut</th>
                                        <th className="p-4 text-right">Visible sur le site</th>
                                        <th className="p-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {properties.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="p-12 text-center text-gray-400">
                                                <div className="flex flex-col items-center gap-4">
                                                    <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-[#EA723D]">
                                                        <Plus size={32} />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-500 mb-1">Aucun bien pour le moment</p>
                                                        <p className="text-sm text-gray-400">Cliquez sur "AJOUTER BIEN" pour saisir votre premier bien</p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {properties.map(property => {
                                        const isPublished = property[publishedKey];

                                        return (
                                            <tr key={property.id} className={`transition-colors ${!isPublished ? 'bg-gray-50/50' : 'hover:bg-amber-50/20'}`}>

                                                {/* ── Bien ── */}
                                                <td className="p-4 flex items-center gap-4">
                                                    <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative border">
                                                        <img src={property.image_url || property.image} alt="" className="w-full h-full object-cover" />
                                                        <div className="absolute top-0 left-0 bg-[#EA723D] text-[8px] font-black text-white px-1.5 py-0.5 rounded-br uppercase">Manuel</div>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-bold text-[#1A2A3A] truncate max-w-[220px] text-sm">{property.title}</p>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[10px] text-gray-400 font-bold uppercase">{property.type}</span>
                                                            {property.is_favorite && <span className="text-[10px] bg-red-50 text-red-500 px-1.5 rounded font-black border border-red-100">❤️ À LA UNE</span>}
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* ── Prix ── */}
                                                <td className="p-4 font-black text-gray-600 text-sm whitespace-nowrap">
                                                    {new Intl.NumberFormat('fr-FR').format(property.price)} €
                                                </td>

                                                {/* ── Ville ── */}
                                                <td className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">{property.city}</td>

                                                {/* ── Type ── */}
                                                <td className="p-4">
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${property.status === 'Vente' ? 'bg-[#1A2A3A] text-white' : 'bg-[#EA723D] text-white'}`}>
                                                        {property.status}
                                                    </span>
                                                </td>

                                                {/* ── Toggle visibilité ── */}
                                                <td className="p-4 text-right">
                                                    <div className="flex justify-end">
                                                        <ToggleSwitch
                                                            active={property[publishedKey]}
                                                            onClick={() => handleTogglePublish(property)}
                                                        />
                                                    </div>
                                                </td>

                                                {/* ── Actions ── */}
                                                <td className="p-4 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <button onClick={() => handleToggleFavorite(property.id)} className={`p-2 rounded text-base transition-transform active:scale-90 ${property.is_favorite ? '' : 'grayscale opacity-30 hover:opacity-100'}`} title="Mettre à la UNE">❤️</button>
                                                        <button onClick={() => navigate(`/admin/edit/${property.id}`)} className="p-2 hover:bg-white rounded text-gray-400 hover:text-[#1A2A3A] transition-colors"><Edit2 size={16} /></button>
                                                        <button onClick={() => handleDelete(property.id)} className="p-2 hover:bg-red-50 rounded text-gray-300 hover:text-red-500 transition-colors" title="Supprimer définitivement"><Trash2 size={16} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                propertyTitle={shareModalConfig.title}
                propertyUrl={shareModalConfig.url}
            />
        </div>
    );
};

export default Dashboard;
